import React, {
  createContext,
  FC,
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { KeyboardArrowLeftRounded } from '@mui/icons-material';
import cx from 'classnames';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { Stack, Tooltip, TooltipProps, Typography } from '@mui/material';
import { ProfileImage } from 'new/elements/ProfileImage';
import { interpolateCSSVariable } from 'new/theme';
import useCollapseAnimation from './useCollapseAnimation';
import styles from './drawer.module.scss';
import AnalyticsUtils from 'js/lib/AnalyticsUtils';
import { useRestrictedTiers } from 'new/hooks/useRestrictedTiers';
import { filterNavigationContentByTier } from 'new/routes/routes.util';

type Props = {
  headerSlot?: (expanded: boolean) => ReactNode;
  content: {
    name?: string;
    badge?: string;
    items: DrawerItemProps[];
  }[];
};

export type DrawerItemProps = {
  title: string;
  to?: string;
  onClick?: () => void;
  Icon?: ReactNode;
  BaseComponent?: FC;
  badge?: string;
  isCollapsed?: boolean;
  isDisabled?: boolean;
};

export const DrawerContext = createContext<{
  toggleCollapse: (state: any) => void;
  drawerContentRef: RefObject<HTMLElement>;
}>({
  toggleCollapse: () => {},
  drawerContentRef: { current: null },
});

const DrawerItem: FC<DrawerItemProps> = (props: DrawerItemProps) => {
  const location = useLocation();
  const { title, Icon, BaseComponent, badge, to, onClick, isCollapsed, isDisabled } = props;
  const Wrapper = BaseComponent || Link;
  const tooltipProps = isCollapsed
    ? ({
        title: title + (!!badge ? ' - ' + badge : ''),
        placement: 'right',
      } as TooltipProps)
    : {};
  const TooltipComponent = isCollapsed ? Tooltip : React.Fragment;
  const IconComponent = (() => {
    if (Icon) return Icon;
    if (isCollapsed && !Icon)
      return <ProfileImage color={'rgba(0,0,0,0.5)'} size="xxsmall" fullName={title} />;
    return null;
  })();

  const isMatchPath = matchPath(location.pathname, { path: to, exact: true });

  const handleClick = (e: React.MouseEvent) => {
    try {
      AnalyticsUtils.captureCustomEvent('drawer_item_clicked', {
        item: typeof title === 'string' ? title : 'no_title',
        path: to,
      });
    } catch (error) {
      console.error('Analytics tracking failed:', error);
    }

    onClick?.();
  };

  return (
    // @ts-ignore
    <TooltipComponent {...tooltipProps}>
      <Wrapper
        to={to || '#'}
        onClick={handleClick}
        className={cx(styles.item, isMatchPath && styles.active, isDisabled && styles.disabled)}
      >
        {!!IconComponent ? <div className={styles.iconContainer}>{IconComponent}</div> : null}
        <Typography className={styles.title} variant="body3">
          {title}{' '}
        </Typography>
        {!!badge && (
          <Typography
            variant="menu2"
            className={styles.badge}
            color={interpolateCSSVariable('--color-warning-700')}
          >
            {badge}
          </Typography>
        )}
      </Wrapper>
    </TooltipComponent>
  );
};

const Drawer: React.FC<React.PropsWithChildren<Props>> = ({ headerSlot, children, content }) => {
  const isNarrowScreen = useMemo(() => window.innerWidth < 1280, []);
  const ref = useRef();
  const hoverTimeout = useRef(null);
  const [hovering, setHovering] = useState(false);
  const animationData = useCollapseAnimation(styles.collapse, !isNarrowScreen);
  const { hasExpressTierLimitedAccess, hasSelfSignupTierLimitedAccess } = useRestrictedTiers();

  const { isOpen, isHover, toggleCollapse } = animationData;

  useEffect(
    () => () => {
      clearTimeout(hoverTimeout.current);
    },
    [],
  );

  const providerValue = useMemo(() => {
    return {
      toggleCollapse,
      drawerContentRef: ref,
    };
  }, [toggleCollapse, ref]);

  const onMouseEnter = () => {
    setHovering(true);
    clearTimeout(hoverTimeout.current);
  };

  const onMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => {
      setHovering(false);
    }, 1000);
  };

  const handledContent = (() => {
    return filterNavigationContentByTier(
      content,
      hasExpressTierLimitedAccess,
      hasSelfSignupTierLimitedAccess,
    );
  })();

  return (
    <section className={styles.wrapper}>
      <nav
        className={cx(
          styles.drawer,
          !isOpen && styles.drawerCollapsed,
          !isOpen && isHover && styles.drawerHover,
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div
          onClick={toggleCollapse}
          className={cx(styles.collapse, {
            [styles.collapse_enter]: hovering,
          })}
        >
          <KeyboardArrowLeftRounded className={styles.collapseIcon} />
        </div>
        <div aria-label="sub-menu-container" className={styles.content}>
          {headerSlot && (
            <Stack flexDirection="row" justifyContent="center" className={styles.headerSlot}>
              {headerSlot(isOpen)}
            </Stack>
          )}
          <div className={styles.options}>
            {handledContent
              .filter((item) => !!item)
              .map(({ name, badge, items }, index) => (
                <React.Fragment key={name || index}>
                  <Typography className={styles.heading} variant="overline">
                    {name}{' '}
                    {!!badge && (
                      <Typography
                        variant="menu2"
                        className={styles.badge}
                        color={interpolateCSSVariable('--color-warning-700')}
                      >
                        {badge}
                      </Typography>
                    )}
                  </Typography>
                  {items
                    .filter((item) => !!item)
                    .map((item) => (
                      <DrawerItem key={item.title} {...item} isCollapsed={!isOpen} />
                    ))}
                </React.Fragment>
              ))}
          </div>
        </div>
      </nav>
      <section className={styles.content} ref={ref}>
        <DrawerContext.Provider value={providerValue}>{children}</DrawerContext.Provider>
      </section>
    </section>
  );
};

export default Drawer;
